import { useNavigate } from 'react-router-dom';
import { Button, Box, Modal, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { getUpdatedScanPdf } from '../utils/api';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '100%',
    maxHeight: '100vh',
    backgroundColor: 'white',
    overflow: 'auto',
    padding: '20px',
    margin: '20px',
    p: 4,
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
};

function ScanResultTable({ open, onClose, title, scanId, userId, scanResult }) {
    const navigate = useNavigate();
    const [loadingPdf, setLoadingPdf] = useState(false);

    const downloadScanPDf = async () => {
        try {
            setLoadingPdf(true);
            await getUpdatedScanPdf(scanId);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingPdf(false);
        }
    };

    const rows = [];
    let headers = [
        { label: "", align: "left", headerColor: "#fff" },
        { label: "Particulars", align: "left", headerColor: "#fff" },
    ];

    headers.push({ label: "Count", align: "left", headerColor: "#fff" });
    headers.push({ label: "Count %", align: "left", headerColor: "#fff" });
    headers.push({ label: "Weight (gm)", align: "left", headerColor: "#fff" });
    headers.push({ label: "Weight (%)", align: "left", headerColor: "#fff" });

    if (scanResult?.kernel_details?.grading) {
        if (scanResult?.kernel_details?.max_limit) {
            headers.push({ label: "% By", align: "left", headerColor: "#fff" });
            headers.push({ label: "Maximum Limit", align: "left", headerColor: "#fff" });
        } else {
            headers.push({ label: "Grade", align: "left", headerColor: "#fff" });
            headers.push({ label: "% By", align: "left", headerColor: "#fff" });
            headers.push({ label: "Range 1", align: "left", headerColor: "#fff" });
            headers.push({ label: "Range 2", align: "left", headerColor: "#fff" });
            headers.push({ label: "Range 3", align: "left", headerColor: "#fff" });
        }
    }

    if (scanResult?.kernel_details?.consider_area) {
        headers.push({ label: "Area (mm2)", align: "left", headerColor: "#fff" });
    }
    if (scanResult?.kernel_details?.consider_length) {
        headers.push({ label: "Length (mm)", align: "left", headerColor: "#fff" });
    }

    const firstRow = [];
    firstRow.push("");
    firstRow.push(scanResult?.kernel_details?.beautify?.translations?.total_kernel_count);
    firstRow.push(scanResult?.kernel_details?.total_kernel_count);
    firstRow.push("100%");

    if (parseFloat(Number(scanResult?.weight_value) ? Number(scanResult?.weight_value) : scanResult?.kernel_details?.total_kernel_weight) > 0) {
        firstRow.push(parseFloat(scanResult?.weight_value) > 0 ? parseFloat(scanResult?.weight_value).toFixed(2) : parseFloat(scanResult?.kernel_details?.total_kernel_weight).toFixed(2));
        firstRow.push("100%");
    }

    if (scanResult?.kernel_details?.grading) {
        if (scanResult?.kernel_details?.max_limit) {
            firstRow.push("NA", "NA");
        } else {
            firstRow.push("NA", "NA", "NA", "NA", "NA");
        }
    }

    if (scanResult?.kernel_details?.consider_area) firstRow.push("");
    if (scanResult?.kernel_details?.consider_length) firstRow.push("");
    rows.push(firstRow);

    scanResult?.kernel_details?.classes?.forEach((key) => {
        let row = [];
        row.push({ cellType: "box", color: scanResult?.processed_image?.encoding_colors[key] });
        row.push(
            scanResult?.kernel_details?.beautify?.translations?.kernel_counts[key] +
            " (" + scanResult?.processed_image?.encodings[key] + ")"
        );
        row.push(Number(scanResult?.kernel_details?.kernel_counts[key]));
        row.push(`${Number((scanResult?.kernel_details?.kernel_counts[key] / scanResult?.kernel_details?.total_kernel_count) * 100).toFixed(2)}%`);

        if (parseFloat(scanResult?.kernel_details?.total_kernel_weight) > 0) {
            let weightKey = scanResult?.kernel_details?.kernel_weights || scanResult?.kernel_details?.kernel_weight;
            const totalWeight = parseFloat(scanResult?.kernel_details?.total_kernel_weight);
            const actualWeight = parseFloat(scanResult?.weight_value || totalWeight);

            if (actualWeight === 0) {
                row.push(Number(weightKey[key]).toFixed(2));
                row.push(((weightKey[key] / totalWeight) * 100).toFixed(2));
            } else {
                const weightedPercentage = (weightKey[key] / totalWeight) * 100;
                const finalWeight = (weightedPercentage * actualWeight) / 100;
                row.push(finalWeight.toFixed(2));
                row.push(weightedPercentage.toFixed(2));
            }
        } else {
            row.push("NA");
            row.push("NA");
        }

        if (scanResult?.kernel_details?.grading) {
            if (scanResult?.kernel_details?.max_limit) {
                let classGrading = scanResult?.kernel_details?.grading[key];
                let rangeValues = classGrading?.weightRange || classGrading?.countRange || ["NA"];
                row.push(classGrading?.weightRange ? 'Weight' : classGrading?.countRange ? 'Count' : 'NA');
                row.push(rangeValues[0]);
            } else {
                const value = scanResult?.kernel_details?.class_ranges[key];
                row.push(value > 0 ? value : 'NA');
                let classGrading = scanResult?.kernel_details?.grading[key];
                let rangeValues = classGrading?.weightRange || classGrading?.countRange || ["NA", "NA", "NA"];
                row.push(classGrading?.weightRange ? 'Weight' : classGrading?.countRange ? 'Count' : 'NA');
                row.push(rangeValues[0]);
                row.push(rangeValues[1]);
                row.push(rangeValues[2]);
            }
        }

        if (scanResult?.kernel_details?.consider_area && scanResult?.kernel_details?.areas) {
            row.push(Number(scanResult?.kernel_details?.areas[key]).toFixed(2));
        }
        if (scanResult?.kernel_details?.consider_length && scanResult?.kernel_details?.kernel_lengths) {
            row.push(Number(scanResult?.kernel_details?.kernel_lengths[key]).toFixed(2));
        }

        rows.push(row);
    });

    const lastRow = [];
    lastRow.push("");
    lastRow.push("Moisture");
    lastRow.push("-");
    lastRow.push("-");
    lastRow.push("-");

    if (scanResult?.moisture_value) {
        lastRow.push(`${scanResult?.moisture_value} %`);
    } else {
        lastRow.push("-");
    }

    if (scanResult?.kernel_details?.grading) {
        let moistureGrading = scanResult?.kernel_details?.grading?.moisture;
        if (scanResult?.kernel_details?.max_limit) {
            let rangeValues = moistureGrading?.weightRange || moistureGrading?.countRange || ["NA"];
            lastRow.push("NA");
            lastRow.push(rangeValues[0] || "NA");
        } else {
            let rangeValues = moistureGrading?.weightRange || moistureGrading?.countRange || ["NA", "NA", "NA"];
            lastRow.push(scanResult?.kernel_details?.moisture_range);
            lastRow.push("NA");
            lastRow.push(rangeValues[0]);
            lastRow.push(rangeValues[1]);
            lastRow.push(rangeValues[2]);
        }
    }

    rows.push(lastRow);

    const table = { headers, rows };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose(event, reason);
    };

    return (
        <Modal open={open} onClose={handleClose} disableEscapeKeyDown>
            <Box style={style}>
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <Typography variant="h6" component="div" style={{ marginLeft: '20px' }}>
                        {title}
                    </Typography>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button
                            variant="contained"
                            onClick={downloadScanPDf}
                            disabled={loadingPdf}
                            startIcon={loadingPdf ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {loadingPdf ? 'Downloading...' : 'Download Updated PDF'}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => navigate(`/users/${userId}`)}
                            startIcon={<ArrowBackIcon />}
                        >
                            Back
                        </Button>
                    </div>
                </div>
                <div>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead style={{ backgroundColor: 'aliceblue', textAlign: "left" }}>
                                <TableRow>
                                    {table.headers.map((header, index) => (
                                        <TableCell key={index} align={header.align}>
                                            {header.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {table.rows.map((row, index) => (
                                    <TableRow key={index} style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                                        {row.map((cell, cellIndex) => (
                                            <TableCell key={cellIndex} align="left" style={{ paddingRight: '50px' }}>
                                                {cell && cell.cellType === 'box' ? (
                                                    <div style={{ backgroundColor: cell.color, width: '20px', height: '20px' }} />
                                                ) : (
                                                    cell
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </Box>
        </Modal>
    );
}

export default ScanResultTable;